import type { Query } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { logger, notify } from '@/shared';

interface QueryToastPlan {
  title: string;
  description?: string;
}

type AnyQuery = Query<unknown, unknown, unknown, readonly unknown[]>;

function getQueryToastPlan(query: AnyQuery): QueryToastPlan | undefined {
  const meta = query.meta as Record<string, unknown> | undefined;
  if (meta?.toastOnError === false) {
    return undefined;
  }

  const toastMeta = meta?.toast as Record<string, unknown> | undefined;
  const titleValue = toastMeta?.title;
  const title =
    typeof titleValue === 'string' && titleValue.trim() !== '' ? titleValue : 'Failed to load data';
  const descriptionValue = toastMeta?.description;
  const description =
    typeof descriptionValue === 'string' && descriptionValue.trim() !== ''
      ? descriptionValue
      : undefined;

  return { title, description };
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const meta = query.meta as Record<string, unknown> | undefined;
      if (meta?.sentryOnError !== false) {
        logger.error(
          'query error',
          {
            surface: 'react-query',
            operation: 'query',
            queryKey: query.queryKey,
            meta: query.meta,
          },
          error
        );
      }

      const plan = getQueryToastPlan(query);
      if (!plan) return;
      notify.error(plan.title, error, { description: plan.description });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      logger.error('mutation error', { surface: 'react-query', operation: 'mutation' }, error);
      notify.error('Request failed', error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      gcTime: 1000 * 60 * 5, // 5 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
